import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchProducts } from "@/lib/api";
import { useStore } from "@/lib/store";
import { ProductCard } from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MOODS, PRIMARY_NOTES, GENDERS } from "@/lib/config";
import { Filter, SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const CONCENTRATIONS = ["Extrait", "EDP", "EDT"];
const SIZE_OPTIONS = [
  { key: "50ml", label: "50ml Bottle", hint: "Signature format" },
  { key: "10ml Sample", label: "10ml Sample", hint: "Try before" },
  { key: "10ml Pure Oil", label: "10ml Pure Oil", hint: "Premium roll-on" },
];
const LONGEVITY = [
  { label: "6+ hours", value: 6 },
  { label: "8+ hours", value: 8 },
  { label: "10+ hours", value: 10 },
];

function FilterPanel({ filters, setFilters, resetFilters }) {
  const toggle = (key, val) => {
    setFilters((f) => ({ ...f, [key]: f[key] === val ? null : val }));
  };
  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/70">Search</div>
        </div>
        <Input
          data-testid="filter-inspired-by-input"
          placeholder="e.g. Tom Ford"
          className="bg-[#FAF8F5] border-black/10 rounded-xl"
          value={filters.inspired_by || ""}
          onChange={(e) => setFilters((f) => ({ ...f, inspired_by: e.target.value || null }))}
        />
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/70 mb-3">Character</div>
        <div className="flex flex-wrap gap-2">
          {GENDERS.map((g) => (
            <button
              key={g.key}
              data-testid={`filter-gender-${g.key}`}
              onClick={() => toggle("gender", g.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filters.gender === g.key ? "bg-[#1A1918] text-[#FAF8F5] border-[#1A1918]" : "bg-[#FAF8F5] border-black/10 hover:border-[#C5A059]/40"
              }`}
            >{g.label}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/70 mb-3">Mood</div>
        <div className="flex flex-wrap gap-2">
          {MOODS.map((m) => (
            <button
              key={m.key}
              data-testid={`filter-mood-${m.key.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
              onClick={() => toggle("mood", m.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filters.mood === m.key ? "bg-[#C5A059]/15 border-[#C5A059] text-[#1A1918]" : "bg-[#FAF8F5] border-black/10 hover:border-[#C5A059]/40"
              }`}
            >{m.label}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/70 mb-3">Note</div>
        <div className="flex flex-wrap gap-2">
          {PRIMARY_NOTES.map((n) => (
            <button
              key={n}
              data-testid={`filter-note-${n.toLowerCase()}`}
              onClick={() => toggle("note", n)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filters.note === n ? "bg-[#2C3E35]/10 border-[#2C3E35]/40 text-[#2C3E35]" : "bg-[#FAF8F5] border-black/10 hover:border-[#C5A059]/40"
              }`}
            >{n}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/70 mb-3">Format</div>
        <div className="flex flex-wrap gap-2">
          {SIZE_OPTIONS.map((s) => {
            const active = filters.size === s.key;
            const isPureOil = s.key === "10ml Pure Oil";
            return (
              <button
                key={s.key}
                data-testid={`filter-size-${s.key.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                onClick={() => toggle("size", s.key)}
                className={`relative text-xs px-3 py-2 rounded-xl border text-left transition-colors min-w-[120px] ${
                  active
                    ? isPureOil
                      ? "bg-gradient-to-br from-[#3B2A1D] to-[#1A1918] text-[#FAF8F5] border-[#C5A059]"
                      : "bg-[#1A1918] text-[#FAF8F5] border-[#1A1918]"
                    : isPureOil
                      ? "bg-[#FAF8F5] border-[#C5A059]/40 hover:border-[#C5A059]"
                      : "bg-[#FAF8F5] border-black/10 hover:border-[#C5A059]/40"
                }`}
              >
                {isPureOil && (
                  <span
                    aria-hidden
                    className={`absolute -top-1.5 right-1.5 text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-medium ${
                      active ? "bg-[#C5A059] text-[#0B0A08]" : "bg-[#C5A059] text-[#FAF8F5]"
                    }`}
                  >
                    Premium
                  </span>
                )}
                <div className="font-medium">{s.label}</div>
                <div className={`text-[10px] uppercase tracking-[0.14em] mt-0.5 ${active ? (isPureOil ? "text-[#C5A059]" : "text-[#FAF8F5]/70") : (isPureOil ? "text-[#C5A059]" : "text-[#1A1918]/55")}`}>{s.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/70 mb-3">Concentration</div>
        <div className="flex flex-wrap gap-2">
          {CONCENTRATIONS.map((c) => (
            <button
              key={c}
              data-testid={`filter-concentration-${c.toLowerCase()}`}
              onClick={() => toggle("concentration", c)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filters.concentration === c ? "bg-[#1A1918] text-[#FAF8F5] border-[#1A1918]" : "bg-[#FAF8F5] border-black/10 hover:border-[#C5A059]/40"
              }`}
            >{c}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/70 mb-3">Longevity</div>
        <div className="flex flex-wrap gap-2">
          {LONGEVITY.map((l) => (
            <button
              key={l.value}
              data-testid={`filter-longevity-${l.value}`}
              onClick={() => toggle("min_longevity", l.value)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filters.min_longevity === l.value ? "bg-[#C5A059]/15 border-[#C5A059]" : "bg-[#FAF8F5] border-black/10 hover:border-[#C5A059]/40"
              }`}
            >{l.label}</button>
          ))}
        </div>
      </div>

      <Button variant="ghost" onClick={resetFilters} data-testid="filter-reset-button" className="text-xs uppercase tracking-[0.14em]">Clear all filters</Button>
    </div>
  );
}

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useStore();
  const [filters, setFilters] = useState({
    inspired_by: searchParams.get("inspired_by"),
    note: searchParams.get("note"),
    mood: searchParams.get("mood"),
    gender: searchParams.get("gender"),
    concentration: searchParams.get("concentration"),
    size: searchParams.get("size"),
    min_longevity: searchParams.get("min_longevity") ? Number(searchParams.get("min_longevity")) : null,
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const resetFilters = () => setFilters({ inspired_by: null, note: null, mood: null, gender: null, concentration: null, size: null, min_longevity: null });

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") params[k] = v; });
    if (country) params.country = country;
    setSearchParams(params);
    setLoading(true);
    fetchProducts(params)
      .then((data) => setItems(data || []))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[shop] fetchProducts failed", err?.response?.status);
        setItems([]);
      })
      .finally(() => setLoading(false));
    // Depend on country too so pricing refetches when currency changes.
  }, [filters, setSearchParams, country]);

  return (
    <main data-testid="shop-page" className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 lg:py-14">
      <div className="mb-6 sm:mb-10">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-2 sm:mb-3">The Collection</div>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#1A1918] leading-[1.05]">All fragrances.</h1>
        <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#1A1918]/70 max-w-2xl">Modern, accessible-luxury impressions of iconic scent profiles. French-inspired composition, hand-poured in Canada, and offered directly — 50ml extraits, pure-oil roll-ons, and discovery samples. Filter by category, size, or the reference you love.</p>

        {/* Gender category tabs — prominent Men / Women / Unisex switch */}
        <div className="mt-6 sm:mt-8 flex flex-wrap gap-2" data-testid="shop-category-tabs">
          {[
            { key: null, label: "All Fragrances" },
            { key: "masculine", label: "For Him" },
            { key: "feminine", label: "For Her" },
            { key: "unisex", label: "Unisex" },
          ].map((cat) => {
            const active = (filters.gender || null) === cat.key;
            return (
              <button
                key={cat.label}
                data-testid={`shop-category-${cat.key || "all"}`}
                onClick={() => setFilters((f) => ({ ...f, gender: cat.key }))}
                className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.16em] border transition-colors ${
                  active
                    ? "bg-[#1A1918] text-[#FAF8F5] border-[#1A1918]"
                    : "bg-transparent text-[#1A1918]/70 border-black/15 hover:border-[#C5A059]/60 hover:text-[#C5A059]"
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="lg:hidden mb-4">
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="shop-filter-drawer-open" variant="outline" className="rounded-xl">
              <SlidersHorizontal className="h-4 w-4 mr-2" /> Filters
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="bg-[#FAF8F5] w-[320px] overflow-y-auto">
            <SheetHeader><SheetTitle className="font-serif text-2xl">Filters</SheetTitle></SheetHeader>
            <div className="mt-6"><FilterPanel filters={filters} setFilters={setFilters} resetFilters={resetFilters} /></div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6 lg:gap-10">
        <aside className="hidden lg:block sticky top-[100px] self-start" data-testid="shop-filter-rail">
          <FilterPanel filters={filters} setFilters={setFilters} resetFilters={resetFilters} />
        </aside>
        <div>
          <div className="text-xs text-[#1A1918]/60 mb-3 sm:mb-4">{items.length} fragrance{items.length === 1 ? "" : "s"}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-3 sm:gap-5">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (<div key={`shop-skel-${i}`} className="aspect-[4/5] rounded-2xl sd-skeleton" />))
              : items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          {!loading && items.length === 0 && (
            <div className="py-24 text-center text-[#1A1918]/60">
              <div className="font-serif text-2xl mb-2">No matches yet.</div>
              <div className="text-sm">Try loosening a filter or clearing your selections.</div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
